import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { generateRandomPassword } from './Util';
import { protectSheetWithRandomPassword } from './ExcelUtils';

export async function excelExport({
  data,
  rowCategoryFunction,
  columnGroupKeyFunction,
  columnGroups,
  fileName = "Report.xlsx",
  grandTotalSubHeaders = [],
  subHeaderStyles = {},
  extraColumns = [],
  dateRange = [],
  loginPlaceId = null
}) {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Report");

  const columnGroupKeys = [...new Set(data.map(columnGroupKeyFunction))];

  const headerStyle = {
    font: { bold: true, color: { argb: 'FFFFFF' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '4472C4' } },
    alignment: { vertical: 'middle', horizontal: 'center' },
    border: {
      top: { style: 'thin' }, bottom: { style: 'thin' },
      left: { style: 'thin' }, right: { style: 'thin' }
    }
  };

  const totalStyle = {
    font: { bold: true, color: { argb: 'FFFFFF' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '5B9BD5' } },
    alignment: { vertical: 'middle', horizontal: 'right' },
    border: {
      top: { style: 'thin' }, bottom: { style: 'thin' },
      left: { style: 'thin' }, right: { style: 'thin' }
    }
  };

  const grandTotalStyle = {
    font: { bold: true, color: { argb: 'FFFFFF' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'ED7D31' } },
    alignment: { vertical: 'middle', horizontal: 'right' },
    border: {
      top: { style: 'thin' }, bottom: { style: 'thin' },
      left: { style: 'thin' }, right: { style: 'thin' }
    }
  };

  let currentRow = 1;
  const totalSubColumnsCount = columnGroups.reduce((acc, group) => acc + group.subColumns.length, 0);
  const grandTotalCount = grandTotalSubHeaders.length;
  const totalColumnsCount = extraColumns.length + columnGroupKeys.length * totalSubColumnsCount + grandTotalCount;

  function addMainHeaders() {
    const headerRow1 = sheet.getRow(currentRow++);
    const headerRow2 = sheet.getRow(currentRow++);

    let currentColIndex = 1;

    extraColumns.forEach(col => {
      sheet.mergeCells(currentRow - 2, currentColIndex, currentRow - 1, currentColIndex);
      const cell = headerRow1.getCell(currentColIndex);
      cell.value = col.label;
      Object.assign(cell, { style: headerStyle });
      currentColIndex++;
    });

    columnGroupKeys.forEach(groupKey => {
      const from = currentColIndex;
      const to = currentColIndex + totalSubColumnsCount - 1;
      sheet.mergeCells(currentRow - 2, from, currentRow - 2, to);
      const cell = headerRow1.getCell(from);
      cell.value = groupKey;
      Object.assign(cell, { style: headerStyle });

      columnGroups.forEach(group => {
        group.subColumns.forEach(sub => {
          const subCell = headerRow2.getCell(currentColIndex);
          subCell.value = sub.label;
          const style = subHeaderStyles[sub.label];
          if (style?.fillColor) {
            subCell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: style.fillColor }
            };
          }
          Object.assign(subCell, { style: headerStyle });
          currentColIndex++;
        });
      });
    });

    if (grandTotalCount > 0) {
      const from = currentColIndex;
      const to = currentColIndex + grandTotalCount - 1;
      sheet.mergeCells(currentRow - 2, from, currentRow - 2, to);
      const cell = headerRow1.getCell(from);
      cell.value = "Grand Total";
      Object.assign(cell, { style: headerStyle });

      grandTotalSubHeaders.forEach((label) => {
        const subCell = headerRow2.getCell(currentColIndex);
        subCell.value = label;
        const style = subHeaderStyles[label];
        if (style?.fillColor) {
          subCell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: style.fillColor }
          };
        }
        Object.assign(subCell, { style: headerStyle });
        currentColIndex++;
      });
    }
  }

  addMainHeaders();

  const groupedData = {};
  data.forEach(item => {
    const branch = item.branch || "Unknown Branch";
    const gn = item.gnName || "Unknown GN";
    const officer = item.fieldOfficerName || "Unknown Officer";

    if (!groupedData[branch]) groupedData[branch] = {};
    if (!groupedData[branch][gn]) groupedData[branch][gn] = {};
    if (!groupedData[branch][gn][officer]) groupedData[branch][gn][officer] = [];

    groupedData[branch][gn][officer].push(item);
  });

  const gnSubtotalRows = [];

  for (const [branch, gnGroups] of Object.entries(groupedData)) {
    const branchSubtotalRows = [];

    const branchRow = sheet.getRow(currentRow++);
    branchRow.getCell(1).value = `Branch: ${branch}`;
    branchRow.getCell(3).value = `Date Range: ${dateRange[0]} to ${dateRange[1]}`;

    [1, 2, 3, 4, 5].forEach(col => {
      const cell = branchRow.getCell(col);
      cell.font = { bold: true, size: 15 };
      cell.alignment = { horizontal: 'left', vertical: 'middle' };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFE5' } };
    });

    for (const [gn, officers] of Object.entries(gnGroups)) {
      const gnDataStartRow = currentRow;
      const officerSubtotalRows = [];

      const gnRow = sheet.getRow(currentRow++);
      sheet.mergeCells(currentRow - 1, 1, currentRow - 1, totalColumnsCount);
      const gnCell = gnRow.getCell(1);
      gnCell.value = `GN Name: ${gn}`;
      gnCell.font = { bold: true, size: 14 };
      gnCell.alignment = { horizontal: 'left', vertical: 'middle' };
      gnCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E1F2' } };

      for (const [officer, officerData] of Object.entries(officers)) {
        const officerDataStartRow = currentRow;
        const officerRow = sheet.getRow(currentRow++);
        sheet.mergeCells(currentRow - 1, 1, currentRow - 1, totalColumnsCount);
        const offCell = officerRow.getCell(1);
        offCell.value = `Officer: ${officer}`;
        offCell.font = { italic: true, bold: true };
        offCell.alignment = { horizontal: 'left', vertical: 'middle' };
        offCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2EFDA' } };

        const pivotMap = {};
        const officerReceipts = new Set();

        officerData.forEach(item => {
          const rowKey = rowCategoryFunction(item);
          const colKey = columnGroupKeyFunction(item);
          if (!pivotMap[rowKey]) pivotMap[rowKey] = {};
          if (!pivotMap[rowKey][colKey]) pivotMap[rowKey][colKey] = [];
          pivotMap[rowKey][colKey].push(item);
          officerReceipts.add(rowKey);
        });

        [...officerReceipts].sort().forEach(receiptNumber => {
          const row = sheet.getRow(currentRow++);
          let cellIndex = 1;

          const itemsForRow = Object.values(pivotMap[receiptNumber] || {}).flat();
          const itemForExtraColumns = itemsForRow[0] || {};
          extraColumns.forEach(col => {
            row.getCell(cellIndex++).value = col.valueExtractor(itemForExtraColumns) || "";
          });

          const rowTotals = {};

          columnGroupKeys.forEach(groupKey => {
            const items = (pivotMap[receiptNumber] && pivotMap[receiptNumber][groupKey]) || [];

            columnGroups.forEach(group => {
              group.subColumns.forEach(sub => {
                const sum = items.reduce((acc, item) => {
                  if (item.type === sub.label) {
                    return acc + (item.amount ? parseFloat(item.amount) : 0);
                  }
                  return acc;
                }, 0);

                if (grandTotalSubHeaders.includes("Total")) {
                  rowTotals["Total"] = (rowTotals["Total"] || 0) + sum;
                } else if (grandTotalSubHeaders.includes(sub.label)) {
                  rowTotals[sub.label] = (rowTotals[sub.label] || 0) + sum;
                }

                row.getCell(cellIndex).value = sum;
                rowTotals[sub.label] = (rowTotals[sub.label] || 0) + sum;
                cellIndex++;
              });
            });
          });

          grandTotalSubHeaders.forEach(label => {
            row.getCell(cellIndex++).value = rowTotals[label] || 0;
          });

          if (currentRow % 2 === 0) {
            row.eachCell(cell => {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFF4F4F4' }
              };
            });
          }
        });

        const officerDataEndRow = currentRow - 1;
        const officerTotalRow = sheet.getRow(currentRow++);
        officerSubtotalRows.push(currentRow - 1);
        officerTotalRow.getCell(1).value = `Subtotal for ${officer}`;
        sheet.mergeCells(currentRow - 1, 1, currentRow - 1, extraColumns.length);
        officerTotalRow.getCell(1).style = totalStyle;

        let cellIndex = extraColumns.length + 1;
        columnGroupKeys.forEach(() => {
          columnGroups.forEach(group => {
            group.subColumns.forEach(() => {
              const columnLetter = sheet.getColumn(cellIndex).letter;
              officerTotalRow.getCell(cellIndex).value = {
                formula: `SUM(${columnLetter}${officerDataStartRow}:${columnLetter}${officerDataEndRow})`
              };
              officerTotalRow.getCell(cellIndex).style = totalStyle;
              cellIndex++;
            });
          });
        });

        grandTotalSubHeaders.forEach(() => {
          const columnLetter = sheet.getColumn(cellIndex).letter;
          officerTotalRow.getCell(cellIndex).value = {
            formula: `SUM(${columnLetter}${officerDataStartRow}:${columnLetter}${officerDataEndRow})`
          };
          officerTotalRow.getCell(cellIndex).style = totalStyle;
          cellIndex++;
        });

        currentRow++;
      }

      const gnTotalRow = sheet.getRow(currentRow++);
      gnSubtotalRows.push(currentRow - 1);
      branchSubtotalRows.push(currentRow - 1);
      gnTotalRow.getCell(1).value = `Subtotal for ${gn}`;
      sheet.mergeCells(currentRow - 1, 1, currentRow - 1, extraColumns.length);
      gnTotalRow.getCell(1).style = totalStyle;

      let cellIndex = extraColumns.length + 1;
      columnGroupKeys.forEach(() => {
        columnGroups.forEach(group => {
          group.subColumns.forEach(() => {
            const colLetter = sheet.getColumn(cellIndex).letter;
            gnTotalRow.getCell(cellIndex).value = {
              formula: `SUM(${officerSubtotalRows.map(r => `${colLetter}${r}`).join(',')})`
            };
            gnTotalRow.getCell(cellIndex).style = totalStyle;
            cellIndex++;
          });
        });
      });

      grandTotalSubHeaders.forEach(() => {
        const colLetter = sheet.getColumn(cellIndex).letter;
        gnTotalRow.getCell(cellIndex).value = {
          formula: `SUM(${officerSubtotalRows.map(r => `${colLetter}${r}`).join(',')})`
        };
        gnTotalRow.getCell(cellIndex).style = totalStyle;
        cellIndex++;
      });

      currentRow++;
    }

    const branchTotalRow = sheet.getRow(currentRow++);
    branchTotalRow.getCell(1).value = `Subtotal for ${branch}`;
    sheet.mergeCells(currentRow - 1, 1, currentRow - 1, extraColumns.length);
    branchTotalRow.getCell(1).style = totalStyle;

    let cellIndex = extraColumns.length + 1;
    columnGroupKeys.forEach(() => {
      columnGroups.forEach(group => {
        group.subColumns.forEach(() => {
          const colLetter = sheet.getColumn(cellIndex).letter;
          branchTotalRow.getCell(cellIndex).value = {
            formula: `SUM(${branchSubtotalRows.map(r => `${colLetter}${r}`).join(',')})`
          };
          branchTotalRow.getCell(cellIndex).style = totalStyle;
          cellIndex++;
        });
      });
    });

    grandTotalSubHeaders.forEach(() => {
      const colLetter = sheet.getColumn(cellIndex).letter;
      branchTotalRow.getCell(cellIndex).value = {
        formula: `SUM(${branchSubtotalRows.map(r => `${colLetter}${r}`).join(',')})`
      };
      branchTotalRow.getCell(cellIndex).style = totalStyle;
      cellIndex++;
    });

    currentRow++;
  }

  const grandTotalRow = sheet.getRow(currentRow++);
  grandTotalRow.getCell(1).value = "GRAND TOTAL";
  sheet.mergeCells(currentRow - 1, 1, currentRow - 1, extraColumns.length);
  grandTotalRow.getCell(1).style = grandTotalStyle;

  let cellIndex = extraColumns.length + 1;
  columnGroupKeys.forEach(() => {
    columnGroups.forEach(group => {
      group.subColumns.forEach(() => {
        const colLetter = sheet.getColumn(cellIndex).letter;
        grandTotalRow.getCell(cellIndex).value = {
          formula: `SUM(${gnSubtotalRows.map(r => `${colLetter}${r}`).join(',')})`
        };
        grandTotalRow.getCell(cellIndex).style = grandTotalStyle;
        cellIndex++;
      });
    });
  });

  grandTotalSubHeaders.forEach(() => {
    const colLetter = sheet.getColumn(cellIndex).letter;
    grandTotalRow.getCell(cellIndex).value = {
      formula: `SUM(${gnSubtotalRows.map(r => `${colLetter}${r}`).join(',')})`
    };
    grandTotalRow.getCell(cellIndex).style = grandTotalStyle;
    cellIndex++;
  });

  sheet.columns.forEach((col, i) => {
    col.width = i < extraColumns.length ? 30 : 20;
    if (i >= extraColumns.length) col.numFmt = '#,##0.00';
  });
  if(loginPlaceId==="1"){
    const password = protectSheetWithRandomPassword(sheet,12);
  }

  sheet.views = [{ state: 'frozen', xSplit: extraColumns.length, ySplit: 2 }];

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  });
  saveAs(blob, fileName);
}
